import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import {
  formatMoney,
  isPurchaseCancelled,
  isPurchaseFailed,
  isPurchasePaid,
  isPurchasePending,
  isPurchaseRefunded,
} from 'features/company/companyUtils';
import { Card, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { localizedText } from 'shared/utilities/format';

export function CompanyPurchaseStatusPage() {
  const { purchaseId } = useParams();
  const { companyId } = useCompany();
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: queryKeys.companyPurchase(purchaseId),
    queryFn: () => companyApi.purchase(companyId, purchaseId),
    refetchInterval: (current) => {
      const purchase = current.state.data?.purchase || current.state.data;
      if (!purchase) {
        return 3000;
      }
      return isPurchasePending(purchase.status) ? 3000 : false;
    },
  });

  useEffect(() => {
    // Never infer payment success from the browser return URL.
  }, []);

  if (query.isLoading) {
    return <Skeleton rows={5} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const purchase = query.data?.purchase || query.data || {};
  const payment = purchase.payment || {};
  const status = purchase.status;

  let title = t('company.purchase.statusPage.pendingTitle');
  let body = payment.instructions || t('company.purchase.statusPage.pendingBody');
  if (isPurchasePaid(status)) {
    title = t('company.purchase.statusPage.successTitle');
    body = t('company.purchase.statusPage.successBody');
  } else if (isPurchaseFailed(status)) {
    title = t('company.purchase.statusPage.failedTitle');
    body = t('company.purchase.statusPage.failedBody');
  } else if (isPurchaseCancelled(status)) {
    title = t('company.purchase.statusPage.cancelledTitle');
    body = t('company.purchase.statusPage.cancelledBody');
  } else if (isPurchaseRefunded(status)) {
    title = t('company.purchase.statusPage.refundedTitle');
    body = t('company.purchase.statusPage.refundedBody');
  }

  return (
    <div className="company-purchase-status">
      <PageMeta titleKey="meta.companyPurchaseStatus" />
      <Card>
        <StatusChip tone={status}>{purchase.status_label || t(`company.purchase.statuses.${status}`)}</StatusChip>
        <h1>{title}</h1>
        <p>{body}</p>
        <dl className="detail-list">
          <div><dt>{t('company.purchase.amount')}</dt><dd>{formatMoney(purchase.total_minor, purchase.currency, locale)}</dd></div>
          <div><dt>{t('company.purchase.plan')}</dt><dd>{localizedText(purchase, locale, ['plan_name', 'name'])}</dd></div>
          {payment.reference || purchase.payment_reference ? (
            <div><dt>{t('company.purchase.reference')}</dt><dd>{payment.reference || purchase.payment_reference}</dd></div>
          ) : null}
        </dl>
        <div className="action-row">
          <Link className="btn btn-primary" to={paths.companyPurchase(purchase.id)}>{t('company.purchase.viewDetails')}</Link>
          {isPurchasePaid(status) ? (
            <Link className="btn btn-secondary" to={paths.companyCodes}>{t('company.purchase.goToCodes')}</Link>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => { void query.refetch(); }}>
                {t('actions.refresh')}
              </button>
              <Link className="btn btn-secondary" to={paths.companyPurchases}>{t('company.purchase.backToPurchases')}</Link>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
